import React, { useState, useEffect } from "react";
import { 
  FiUser, 
  FiMail, 
  FiPhone, 
  FiBell, 
  FiSave,
  FiSettings,
  FiCheck,
  FiX
} from "react-icons/fi";
import ApiService from "../../services/apiService";
import "./settings.css";

interface UserSettings {
  mobile: string;
  telephone: string;
  email: string;
  alternateEmail: string;
  notifications: {
    zones: boolean;
    snagging: boolean;
    projectUpdates: boolean;
    systemAlerts: boolean;
  };
}

const Settings: React.FC = () => {

  const [settings, setSettings] = useState<UserSettings>({
    mobile: "",
    telephone: "",
    email: "",
    alternateEmail: "",
    notifications: {
      zones: false,
      snagging: true,
      projectUpdates: true,
      systemAlerts: false,
    }
  });

  const [isLoading, setIsLoading] = useState(false);
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'success' | 'error'>('idle');
  const [errors, setErrors] = useState<{[key: string]: string}>({});

  // Load user settings on component mount
  useEffect(() => {
    loadUserSettings();
  }, []);

  const loadUserSettings = async () => {
    setIsLoading(true);
    try {
      const profile: any = await ApiService.getProfile();
      setSettings(prev => ({
        ...prev,
        email: profile?.email || '',
      }));
    } catch (error) {
      console.error('Failed to load settings:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const validateForm = (): boolean => {
    const newErrors: {[key: string]: string} = {};

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (settings.email && !emailRegex.test(settings.email)) {
      newErrors.email = "Please enter a valid email address";
    }
    if (settings.alternateEmail && !emailRegex.test(settings.alternateEmail)) {
      newErrors.alternateEmail = "Please enter a valid alternate email address";
    }

    // Phone validation
    const phoneRegex = /^[+]?[1-9]\d{1,14}$/;
    if (settings.mobile && !phoneRegex.test(settings.mobile.replace(/\s/g, ''))) {
      newErrors.mobile = "Please enter a valid mobile number";
    }
    if (settings.telephone && !phoneRegex.test(settings.telephone.replace(/\s/g, ''))) {
      newErrors.telephone = "Please enter a valid telephone number";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (field: keyof UserSettings, value: string) => {
    setSettings(prev => ({
      ...prev,
      [field]: value
    }));

    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const handleNotificationChange = (field: keyof UserSettings['notifications'], value: boolean) => {
    setSettings(prev => ({
      ...prev,
      notifications: {
        ...prev.notifications,
        [field]: value
      }
    }));
  };

  const saveSettings = async () => {
    if (!validateForm()) {
      return;
    }

    setSaveStatus('saving');
    try {
      await ApiService.updateProfile({
        email: settings.email,
      });

      setSaveStatus('success');
      setTimeout(() => setSaveStatus('idle'), 3000);
    } catch (error) {
      console.error('Failed to save settings:', error);
      setSaveStatus('error');
      setTimeout(() => setSaveStatus('idle'), 3000);
    }
  };

  const resetToDefaults = () => {
    setSettings({
      mobile: "",
      telephone: "",
      email: "",
      alternateEmail: "",
      notifications: {
        zones: false,
        snagging: true,
        projectUpdates: true,
        systemAlerts: false,
      }
    });
    setErrors({});
  };

  return (
    <div className="settings-container">
      <div className="settings-header">
        <div className="header-content">
          <div className="header-icon">
            <FiSettings size={32} />
          </div>
          <div>
            <h1 className="settings-title">Account Settings</h1>
            <p className="settings-subtitle">Manage your contact information and notification preferences</p>
          </div>
        </div>
      </div>

      <div className="settings-content">
        {/* Contact Information Section */}
        <div className="settings-section">
          <div className="section-header">
            <FiUser className="section-icon" size={24} />
            <h2 className="section-title">Contact Information</h2>
          </div>

          <div className="settings-grid">
            <div className="form-group">
              <label className="form-label">
                <FiPhone size={16} />
                Mobile Number
              </label>
              <input
                type="tel"
                className={`form-input ${errors.mobile ? 'error' : ''}`}
                value={settings.mobile}
                onChange={(e) => handleInputChange('mobile', e.target.value)}
                placeholder="+1 234 567 8900"
                disabled={isLoading}
              />
              {errors.mobile && <span className="error-message">{errors.mobile}</span>}
            </div>

            <div className="form-group">
              <label className="form-label">
                <FiPhone size={16} />
                Telephone Number
              </label>
              <input
                type="tel"
                className={`form-input ${errors.telephone ? 'error' : ''}`}
                value={settings.telephone}
                onChange={(e) => handleInputChange('telephone', e.target.value)}
                placeholder="+1 234 567 8900"
                disabled={isLoading}
              />
              {errors.telephone && <span className="error-message">{errors.telephone}</span>}
            </div>

            <div className="form-group">
              <label className="form-label">
                <FiMail size={16} />
                Primary Email
              </label>
              <input
                type="email"
                className={`form-input ${errors.email ? 'error' : ''}`}
                value={settings.email}
                onChange={(e) => handleInputChange('email', e.target.value)}
                placeholder="user@example.com"
                disabled={isLoading}
              />
              {errors.email && <span className="error-message">{errors.email}</span>}
            </div>

            <div className="form-group">
              <label className="form-label">
                <FiMail size={16} />
                Alternate Email
              </label>
              <input
                type="email"
                className={`form-input ${errors.alternateEmail ? 'error' : ''}`}
                value={settings.alternateEmail}
                onChange={(e) => handleInputChange('alternateEmail', e.target.value)}
                placeholder="backup@example.com"
                disabled={isLoading}
              />
              {errors.alternateEmail && <span className="error-message">{errors.alternateEmail}</span>}
            </div>
          </div>
        </div>

        {/* Notifications Section */}
        <div className="settings-section">
          <div className="section-header">
            <FiBell className="section-icon" size={24} />
            <h2 className="section-title">Notification Preferences</h2>
          </div>

          <div className="notifications-grid">
            <div className="notification-item">
              <div className="notification-info">
                <h3 className="notification-title">Zone Notifications</h3>
                <p className="notification-description">
                  Receive alerts when activities occur in your assigned zones
                </p>
              </div>
              <label className="toggle-switch">
                <input
                  type="checkbox"
                  checked={settings.notifications.zones}
                  onChange={(e) => handleNotificationChange('zones', e.target.checked)}
                  disabled={isLoading}
                />
                <span className="toggle-slider"></span>
              </label>
            </div>

            <div className="notification-item">
              <div className="notification-info">
                <h3 className="notification-title">Snagging Updates</h3>
                <p className="notification-description">
                  Get notified about new snagging issues and status changes
                </p>
              </div>
              <label className="toggle-switch">
                <input
                  type="checkbox"
                  checked={settings.notifications.snagging}
                  onChange={(e) => handleNotificationChange('snagging', e.target.checked)}
                  disabled={isLoading}
                />
                <span className="toggle-slider"></span>
              </label>
            </div>

            <div className="notification-item">
              <div className="notification-info">
                <h3 className="notification-title">Project Updates</h3>
                <p className="notification-description">
                  Stay informed about project milestone completions and timeline changes
                </p>
              </div>
              <label className="toggle-switch">
                <input
                  type="checkbox"
                  checked={settings.notifications.projectUpdates}
                  onChange={(e) => handleNotificationChange('projectUpdates', e.target.checked)}
                  disabled={isLoading}
                />
                <span className="toggle-slider"></span>
              </label>
            </div>

            <div className="notification-item">
              <div className="notification-info">
                <h3 className="notification-title">System Alerts</h3>
                <p className="notification-description">
                  Receive important system maintenance and security notifications
                </p>
              </div>
              <label className="toggle-switch">
                <input
                  type="checkbox"
                  checked={settings.notifications.systemAlerts}
                  onChange={(e) => handleNotificationChange('systemAlerts', e.target.checked)}
                  disabled={isLoading}
                />
                <span className="toggle-slider"></span>
              </label>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="settings-actions">
          <button 
            className="btn-secondary" 
            onClick={resetToDefaults}
            disabled={isLoading || saveStatus === 'saving'}
          >
            Reset to Defaults
          </button>

          <button 
            className="btn-primary save-btn" 
            onClick={saveSettings}
            disabled={isLoading || saveStatus === 'saving'}
          >
            {saveStatus === 'saving' && <div className="loading-spinner"></div>}
            {saveStatus === 'success' && <FiCheck size={16} />}
            {saveStatus === 'error' && <FiX size={16} />}
            {saveStatus === 'idle' && <FiSave size={16} />}

            {saveStatus === 'saving' && 'Saving...'}
            {saveStatus === 'success' && 'Saved!'}
            {saveStatus === 'error' && 'Error'}
            {saveStatus === 'idle' && 'Save Settings'}
          </button>
        </div>

        {/* Save Status Messages */}
        {saveStatus === 'success' && (
          <div className="status-message success">
            <FiCheck size={16} />
            Settings saved successfully!
          </div>
        )}

        {saveStatus === 'error' && (
          <div className="status-message error">
            <FiX size={16} />
            Failed to save settings. Please try again.
          </div>
        )}
      </div>
    </div>
  );
};

export default Settings;